import { type FC, type ReactNode } from "react";
import {
  Box,
  Button,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { useSnackbar } from "notistack";
import { useDateFormat, useTranslation } from "@/shared/hooks";
import { formatCurrency } from "@/shared/utils/formatCurrency";
import { getMinPrice } from "@/shared/utils/getMinPrice";
import { Link } from "@/shared/components/RouteDialog";
import {
  ContentTypeType,
  ProductTypeType,
  StreamStatusType,
  type ContentQueryType,
} from "@/types/graphql/site.generated";

// Порт core/components/PaymentScreen/PaymentScreen.js +
// core/components/PaymentButtons/PaymentButtons.js: экран пейволла для
// CONTENT_IS_NOT_AVAILABLE_FOR_FREE_VIEWING (Content.access, backend Task 4)
// — фон-превью контента + затемнение (та же вёрстка, что AccessErrorScreen.tsx,
// maxWidth 800 по центру, как в референсе). Отличия от референса: нет
// DONATION (нет такого ProductType в схеме Task 1/4), нет BOOK (ContentType
// сайта — VIDEO|STREAM|COLLECTION, поэтому нет и постоянного заголовка
// референса — он рендерится только для BOOK), нет ChooseProductDialog
// (несколько продуктов одного типа — просто берём минимальную цену, без
// диалога выбора — вне скоупа Task 9), клик по цене — снекбар-заглушка
// product.paymentUnavailable (Task 8), а не реальная покупка (домен покупок
// ещё не перенесён).
export type PaymentScreenProduct =
  ContentQueryType["content"]["products"]["nodes"][number];
export type PaymentScreenStream = ContentQueryType["content"]["stream"];

interface ProductButton {
  /** Самый дешёвый продукт типа: его цена идёт в текст кнопки. */
  product: PaymentScreenProduct;
  minPrice: PaymentScreenProduct["prices"][number];
  /** Все продукты этого типа с ценой (референсный isMany: клик открывает
   * выбор продукта, если их больше одного). */
  products: PaymentScreenProduct[];
}

export function getPaymentButtons(
  products: PaymentScreenProduct[],
): ProductButton[] {
  const withMinPrice = products
    .map((product) => {
      const minPrice = getMinPrice(product.prices);
      return minPrice ? { product, minPrice } : null;
    })
    .filter((b): b is Omit<ProductButton, "products"> => b !== null);

  const pick = (type: ProductTypeType): ProductButton | undefined => {
    const items = withMinPrice
      .filter((b) => b.product.type === type)
      .sort((a, b) => a.minPrice.value - b.minPrice.value);
    const cheapest = items[0];
    if (!cheapest) return undefined;
    return { ...cheapest, products: items.map((item) => item.product) };
  };

  return [
    pick(ProductTypeType.SubscriptionType),
    pick(ProductTypeType.PpvType),
  ].filter((b): b is ProductButton => b !== undefined);
}

export function getPaymentButtonText(
  t: ReturnType<typeof useTranslation>["t"],
  product: PaymentScreenProduct,
  minPrice: ProductButton["minPrice"],
  hasMultipleProducts = false,
): string {
  const isSubscription = product.type === ProductTypeType.SubscriptionType;

  if (isSubscription && minPrice.value === 0) {
    return t("paymentScreen.subscribeFree");
  }

  const value = formatCurrency(minPrice.value, minPrice.currency);
  const hasMultiplePrices = product.prices.length > 1 || hasMultipleProducts;

  if (isSubscription) {
    return t(
      hasMultiplePrices
        ? "paymentScreen.subscribeFrom"
        : "paymentScreen.subscribe",
      { value },
    );
  }

  return t(
    hasMultiplePrices
      ? "paymentScreen.watchOnlineFrom"
      : "paymentScreen.watchOnline",
    { value },
  );
}

// Порт PaymentScreen.js:55-89 (getSubtitle), ограниченный веткам VIDEO
// (будущий accessStartAt) и STREAM (по stream.status) — ветка BOOK
// отсутствует (нет такого ContentType). Если контент — STREAM, но поле
// stream почему-то не пришло, используется общий фолбэк-текст ниже.
function getSubtitle(
  t: ReturnType<typeof useTranslation>["t"],
  formatDateTime: ReturnType<typeof useDateFormat>["formatDateTime"],
  contentType: ContentQueryType["content"]["type"],
  accessStartAt: string | null | undefined,
  stream: PaymentScreenStream | undefined,
): string {
  if (contentType === ContentTypeType.VideoType) {
    if (accessStartAt && new Date(accessStartAt) > new Date()) {
      return t("player.accessError.availableAt", {
        date: formatDateTime(accessStartAt),
      });
    }
  }

  if (contentType === ContentTypeType.StreamType && stream) {
    switch (stream.status) {
      case StreamStatusType.StartedType:
        return t("paymentScreen.stream.started");
      case StreamStatusType.PendingType:
        if (stream.startAt) {
          return t("paymentScreen.stream.textWithDate", {
            date: formatDateTime(stream.startAt),
          });
        }
        break;
      case StreamStatusType.CancelledType:
        return t("stream.cancelled.title");
      case StreamStatusType.FinishedType:
        return t("paymentScreen.stream.finished", {
          date: stream.finishAt ? formatDateTime(stream.finishAt) : "",
        });
      default:
        break;
    }
  }

  return t("paymentScreen.subtitle");
}

const Background: FC<{ poster: string | null; children: ReactNode }> = ({
  poster,
  children,
}) => (
  <Box
    sx={{
      position: "relative",
      width: "100%",
      aspectRatio: "16 / 9",
      borderRadius: 2,
      overflow: "hidden",
      backgroundColor: "#000",
      backgroundImage: poster ? `url(${poster})` : undefined,
      backgroundSize: "cover",
      backgroundPosition: "center",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // Референс: rgba(0,0,0, dark ? 0.75 : 0.1) — но текст здесь всегда
        // белый (#fff) в обеих темах, и у нас нет тёмного варианта текста
        // для светлой темы. При overlay 0.1 в светлой теме белый текст на
        // светлом постере становится нечитаемым, поэтому фиксированная
        // затемнённость 0.65 сохранена в обеих темах (см.
        // products-site-fixes-report.md, п.16).
        backgroundColor: "rgba(0,0,0,0.65)",
        color: "#fff",
        textAlign: "center",
        px: 2,
      }}
    >
      {children}
    </Box>
  </Box>
);

export const PaymentScreen: FC<{
  poster: string | null;
  /** Тип контента и accessStartAt/stream — только чтобы решить, какой
   * подзаголовок показать (référence §3.2 спеки: ветки VIDEO/STREAM). */
  contentType: ContentQueryType["content"]["type"];
  accessStartAt?: string | null;
  stream?: PaymentScreenStream;
  products: PaymentScreenProduct[];
  /** Клик по цене: родитель открывает диалог оплаты (как в референсном
   * onClickProductButton). Без колбэка (embed) остаётся подсказка. */
  onProductClick?: (products: PaymentScreenProduct[]) => void;
  /** Кнопка «Разобраться с помощником» (Task 15), слотом от ContentPlayer,
   * см. AccessErrorScreen.tsx. */
  assistantAction?: ReactNode;
}> = ({
  poster,
  contentType,
  accessStartAt,
  stream,
  products,
  onProductClick,
  assistantAction,
}) => {
  const { t } = useTranslation();
  const { formatDateTime } = useDateFormat();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("sm"));

  const buttons = getPaymentButtons(products);

  const subtitle = getSubtitle(
    t,
    formatDateTime,
    contentType,
    accessStartAt,
    stream,
  );

  const handlePriceClick = (products: PaymentScreenProduct[]) => {
    if (onProductClick) return onProductClick(products);
    enqueueSnackbar(t("product.paymentUnavailable"), { variant: "info" });
  };

  if (buttons.length === 0) {
    return (
      <Background poster={poster}>
        <Stack spacing={1.5} alignItems="center">
          <LockIcon sx={{ fontSize: 48 }} />
          <Typography variant="h6">
            {t("paymentScreen.productError.title")}
          </Typography>
          <Typography variant="body2">
            {t("paymentScreen.productError.text")}
          </Typography>
          {assistantAction}
        </Stack>
      </Background>
    );
  }

  return (
    <Background poster={poster}>
      <Box sx={{ maxWidth: 800, width: "100%" }}>
        {/* Референс показывает постоянный заголовок только для BOOK — у нас
            такого ContentType нет, поэтому заголовок здесь не рендерится
            (PaymentScreen.js:120-138), остаётся только подзаголовок. */}
        <Typography variant="body2" align="center" sx={{ mb: 3 }}>
          {subtitle}
        </Typography>
        <Grid container spacing={smUp ? 3 : 2} justifyContent="center">
          {buttons.map(({ product, minPrice, products: group }) => (
            <Grid key={product.id}>
              <Stack spacing={1} alignItems="center">
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  onClick={() => handlePriceClick(group)}
                >
                  {getPaymentButtonText(t, product, minPrice, group.length > 1)}
                </Button>
                <Typography
                  component={Link}
                  href={`/product/${product.id}`}
                  variant="body2"
                  sx={{ color: "inherit", textDecoration: "underline" }}
                >
                  {t("paymentScreen.moreAboutProduct")}
                </Typography>
              </Stack>
            </Grid>
          ))}
        </Grid>
        {assistantAction && (
          <Box sx={{ mt: 2, textAlign: "center" }}>{assistantAction}</Box>
        )}
      </Box>
    </Background>
  );
};

// Embed: только текст, без кнопок (как остальные access-ошибки в embed —
// см. AccessErrorScreen.tsx, использующийся и для SITE, и для EMBED
// одинаково для прочих кодов ошибок доступа).
export const EmbedPaymentScreen: FC<{ poster: string | null }> = ({
  poster,
}) => {
  const { t } = useTranslation();

  return (
    <Background poster={poster}>
      <Stack spacing={1.5} alignItems="center">
        <LockIcon sx={{ fontSize: 48 }} />
        <Typography variant="h6">{t("paymentScreen.title")}</Typography>
      </Stack>
    </Background>
  );
};
